import { CustomEventWithDetail, html, View } from 'rune-ts';
import { makeSubScreenNavigate, PostMessageNavigate } from '../../../../shared/app/navigate';
import { preventEscape } from '../../../../shared/util/preventEscape';
import { Confirm } from '../Confirm/Confirm';
import klass from './ProductCardView.module.scss';
import { StarIcon } from '../../atoms/Icon';
import { classes } from '../../../../shared/util';
import {
  ProductCardOption,
  ProductCardSize,
  ProductCardViewData,
} from '../../../../features/ProductList/type';
import { ProductStoreName } from './view/ProductStoreName/ProductStoreName';
import { ProductLikeButton } from './view/ProductLikeButton/ProductLikeButton';
import { MShopUtilF } from '@modules/MShop/Util/F/Function/module/MShopUtilF';
import { pushLoginStack } from '@modules/Creator/Login/F/fs';
import { product_tag } from '../../../../features/ProductList/constant';
import { differenceInMilliseconds } from 'date-fns';
import { ProductCardHelper } from './ProductCardHelper';
import { ProductCardPrice } from '../ProductCardPrice/ProductCardPrice';
import { dataStr } from '../../../../shared/util/dataStr';
import { ProductCardName } from './view/ProductCardName/ProductCardName';
import { ProductTagFactory } from '../ProductTag/ProductTagFactory';
import {
  ProductTagBase,
  ProductTagBaseProps,
  ProductTagOption,
  ProductTagRadius,
} from '../ProductTag/ProductTagBase';
import { updateLikeMarppleShop } from '@modules/MShop/App/Product/Item/F/Function/updateLikeMarppleShop';
import { ProductCardStyler } from './utils/ProductCardStyler';
import { ProductCardVisibilityManager } from '@services/MarppleShop/renderApp/components/cells/ProductCard/utils/ProductCardVisibilityManager.ts';
import { isDate, isNumber, isString, isUndefined } from '@fxts/core';
import { ProductThumbnailView } from '@services/MarppleShop/renderApp/components/cells/ProductThumbnail/ProductThumbnailView.ts';
import { ProductBadgeListView } from '@services/MarppleShop/renderApp/components/cells/ProductBadge/ProductBadgeListView.ts';
import {
  createProductListBadges,
  ProductBadgeView,
} from '@services/MarppleShop/renderApp/components/cells/ProductBadge';
import { isLegacyStockProduct } from '@services/MarppleShop/features/ProductList';
import { PeriodLimitedTag } from '@services/MarppleShop/renderApp/components/cells/ProductTag/tags/PeriodLimitedTag.ts';

export class ProductLikeToggleEvent extends CustomEventWithDetail<{
  toggleLikeButton: () => void;
}> {}

export class ProductCardView extends View<ProductCardViewData> {
  state: {
    img_alt: string;
    post_message: PostMessageNavigate;
    stores_product_id: number;
  };

  LikeButton: ProductLikeButton | null;

  productBadgeListView?: ProductBadgeListView;

  rankingBadgeHtml?: ProductBadgeView;

  visibilityManager: ProductCardVisibilityManager;

  TagView?: ProductTagBase;

  private readonly size: ProductCardSize;

  private readonly styler: ProductCardStyler;

  private realtimeTagOption: ProductTagBaseProps;

  // 기간한정 상품인지
  readonly is_period_limited: boolean;

  // 수량한정 상품인지
  readonly is_quantity_limited: boolean;

  constructor(
    data: ProductCardViewData,
    private option: ProductCardOption,
  ) {
    super(data, option);

    // rune에서 date -> string 으로 변환되는 이슈 있어서 parse
    if (isString(this.data.sell_start_at)) {
      this.data.sell_start_at = new Date(this.data.sell_start_at);
    }
    if (isString(this.data.sell_end_at)) {
      this.data.sell_end_at = new Date(this.data.sell_end_at);
    }
    if (isString(this.data.ordered_at)) {
      this.data.ordered_at = new Date(this.data.ordered_at);
    }
    if (isString(this.data.public_at)) {
      this.data.public_at = new Date(this.data.public_at);
    }

    this.size = option.size;

    this.styler = new ProductCardStyler(this.size, data, option);

    this.visibilityManager = new ProductCardVisibilityManager(this.size, this.data, this.option);

    this.LikeButton = this.visibilityManager.isLikeButton()
      ? new ProductLikeButton(this.data, {
          theme: this.styler.theme,
          klass: this.option.hide_info ? `${klass.hide_info} ${klass.like_btn}` : klass.like_btn,
        })
      : null;

    this.is_period_limited = ProductCardHelper.isPeriodLimited(this.data);
    this.is_quantity_limited = ProductCardHelper.isQuantityLimited(this.data);

    this.setBadges();
    this.setTag();

    this.state = {
      img_alt: this.makeImgAlt(),
      post_message: this.makePostMessage(),
      stores_product_id: this.data.stores_product_id,
    };

    this.realtimeTagOption = this.option.realtime_tag
      ? this.option.realtime_tag
      : {
          lang: ET.lang,
          ...this.styler.realtimeTag,
        };

    if (isUndefined(this.data.user_id)) {
      console.warn(`[ProductCardView] this.data.user_id is required.`);
    }
  }

  override template() {
    const style = {
      root: this.option.card_klass || '',
      custom: this.option.klass || '',
      theme: this.styler.klass.theme,
      hidden: this.styler.klass.hidden,
      direction: this.styler.klass.direction,
      sold_out: this.styler.klass.soldOut,
      size: this.styler.klass.cardSize,
    };

    return html`<div
      class="${klass.product_card} ${style.root} ${style.custom} ${style.theme} ${style.direction} ${style.hidden} ${style.size}"
      style="${this.styler.injectedStyles}"
    >
      <a
        class="${klass.thumbnail_container} ${style.sold_out}"
        href="${this.makeLink()}"
        data-post-message="${dataStr(this.state.post_message)}"
      >
        ${this.rankingBadgeHtml ?? ''} ${this.productBadgeListView ?? ''}

        <!-- 상품 썸네일 -->
        ${new ProductThumbnailView({
          thumbnail: this.data.thumbnail_info,
          options: {
            klass: this.option.klass,
            img_alt: this.option.img_alt,
            is_lazy: this.option.is_lazy,
            resize_width: this.option.resize_width,
            without_max_style: this.option.without_max_style,
          },
        })}

        <!-- hide_info: true => 좋아요 버튼 우측 상단에 위치 -->
        ${this.option.hide_info ? (this.LikeButton ?? '') : ''}
      </a>

      ${this.option.hide_info
        ? ''
        : html`<div class="${klass.info_container}">
            <div class="${klass.info}">
              <!-- 실시간 태그 -->
              ${this.visibilityManager.isRealtimeTag() && isDate(this.data.ordered_at) // TODO: isRealtimeTag 타입가드 외않되
                ? html`<div
                    class="${klass.top_tag_wrap} ${classes({
                      [klass.stretch]:
                        this.realtimeTagOption.options &&
                        this.realtimeTagOption.options.includes(ProductTagOption.stretch),
                    })}"
                  >
                    ${ProductTagFactory.create(
                      'realtime',
                      new Date(this.data.ordered_at),
                      this.realtimeTagOption,
                    )}
                  </div>`
                : ''}

              <!-- n명이 보는 중 태그 -->
              ${this.visibilityManager.isWatcherCountTag() && isNumber(this.data.watcher_count)
                ? html`<div class="${klass.top_tag_wrap}">
                    ${ProductTagFactory.create('watcher_count', this.data.watcher_count, {
                      lang: ET.lang,
                      size: this.styler.watcherCountTagSize,
                      radius: ProductTagRadius.none,
                      options: [ProductTagOption.stretch, ProductTagOption.fit_content],
                    })}
                  </div>`
                : ''}

              <div class="${klass.store_name_wrap}">
                <!-- 스토어명 -->
                ${new ProductStoreName(this.data, {
                  klass: this.styler.fontSize.store_name,
                })}

                <!-- hide_info: false => 좋아요 버튼 info에 위치 -->
                ${this.LikeButton ?? ''}
              </div>

              <!-- 상품명 -->
              <span class="${klass.product_name_wrap}">
                ${new ProductCardName(this.data, {
                  klass: this.styler.fontSize.product_name,
                })}
              </span>

              <!-- 가격 -->
              <span class="${klass.price_wrap}">
                ${new ProductCardPrice(
                  {
                    price: this.data.price,
                    price_en: this.data.price_en,
                    price_jp: this.data.price_jp,
                  },
                  {
                    price_klass: this.styler.fontSize.price,
                    currency_klass: this.styler.fontSize.currency,
                  },
                )}
              </span>

              <!-- 리뷰 카운트, 평점 -->
              ${this.visibilityManager.isReview()
                ? html`
                    <a
                      href="${this.makeLink()}"
                      data-post-message="${dataStr(this.state.post_message)}"
                      class="${this.styler.reviewWrap} ${style.theme}"
                    >
                      <span class="${this.styler.reviewIcon}">${StarIcon}</span>
                      <span class="${this.styler.fontSize.review}">
                        ${this.data.review_score.toFixed(1)}
                      </span>
                      <span class="${this.styler.fontSize.review}">(${this.data.review_count})</span>
                    </a>
                  `
                : ''}

              <!-- 하단 태그 -->
              ${this.visibilityManager.isBottomTag()
                ? html`<div class="${klass.bottom_tag_wrap}">${this.TagView || ''}</div>`
                : ''}
            </div>
          </div>`}
    </div>`;
  }

  public fadeIn() {
    this.option.is_hidden = false;

    this.element().offsetWidth;
    this.element().classList.remove(klass.hidden);
    this.element().classList.add(klass.fade_in);
  }

  private makeLink() {
    return `/${this.data.domain_name}/products/${this.data.id}`;
  }

  private makeImgAlt(): string {
    const { name, store_name } = this.data;
    const kr_suffix = T.lang === 'kr' ? ' 굿즈, 굿즈 판매, 굿즈샵' : '';
    return `${store_name}, ${name}${kr_suffix}`;
  }

  override onRender() {
    if (this.isUpcoming()) {
      const diff_ms = differenceInMilliseconds(this.data.sell_start_at, new Date());
      setTimeout(() => {
        this.setBadges();
        this.setTag();
        this.redraw();
      }, diff_ms + 1000);
    } else if (this.isPeriodLimited() && !this.isSoldOut()) {
      const diff_ms = differenceInMilliseconds(this.data.sell_end_at, new Date());
      setTimeout(() => {
        this.setBadges();
        this.setTag();
        this.redraw();
      }, diff_ms + 1000);
    }

    this.delegate('click', `.${klass.like_btn}`, (e) => {
      e.originalEvent.preventDefault();
      this.dispatchEvent(ProductLikeToggleEvent, {
        bubbles: true,
        detail: {
          toggleLikeButton: this.toggleLikeButton.bind(this),
        },
      });
    });
  }

  public async toggleLikeButton() {
    // TODO: window.box 로직 제거
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (!this.data.user_id || window.box.sel('is_user->type') == 'TEMP') {
      const confirmed = (await Confirm.open({
        title: ET('mps2::login::need_login::title'),
        message: preventEscape(ET('mps2::login::need_login::message')),
        confirmText: ET('mps2::login::login'),
        cancelText: ET('mps2::signup::close'),
      })) as boolean;

      if (confirmed) {
        if (MShopUtilF.isApp()) {
          pushLoginStack();
        } else {
          location.href = `/${T.lang}/@/login?url=${location.pathname + location.search}`;
        }
      }
    } else {
      this.data.is_liked = !this.data.is_liked;

      if (this.LikeButton) {
        this.LikeButton.setActivation(this.data.is_liked);
        this.LikeButton.redraw();
      }
      updateLikeMarppleShop(!this.data.is_liked, this.data.stores_product_id);
    }
  }

  private setBadges(): void {
    if (!this.option.hide_badges) {
      const badges = createProductListBadges(
        {
          stock: this.data.stock,
          legacyStock: this.data.legacyStock,
          goodsTypeId: this.data.goods_type_id,
          outOfStock: this.data.out_of_stock,
          publicAt: this.data.public_at,
          salePeriod: {
            startDate: this.data.sell_start_at ?? undefined,
            endDate: this.data.sell_end_at ?? undefined,
          },
          status: {
            isPublic: this.data.is_public,
            isMembership: this.data.is_membership,
            isFunding: this.data.is_funding,
            isLiked: this.data.is_liked,
            isNft: this.data.is_token_gate,
            isBrandDeal: this.data.is_brand_deal,
            isPopular: this.data.is_popular,
            isTrending: this.data.is_trending,
          },
          limitedMeta: this.data.limitedMeta,
          lowStockMeta: this.data.lowStockMeta,
        },
        Date.now(),
      );

      this.productBadgeListView = new ProductBadgeListView({
        badges,
        options: {
          max_length: 2,
          size: this.styler.badgeSize,
          lang: ET.lang,
          styles: {
            container: klass.badge_container,
          },
          baseTimestamp: Date.now(),
        },
      });

      // `Ranking` 랭킹, 주입한 경우에만 표시
      if (this.data.ranking) {
        this.rankingBadgeHtml = new ProductBadgeView({
          type: 'Rank',
          rank: this.data.ranking,
          options: {
            klass: klass.ranking_badge,
            size: this.styler.rankBadgeSize,
            lang: ET.lang,
            baseTimestamp: Date.now(),
          },
        });
      }
    }
  }

  private setTag(): void {
    if (!this.visibilityManager.isBottomTag()) {
      return;
    }

    const props: ProductTagBaseProps = {
      lang: ET.lang,
      size: this.styler.bottomTagSize,
    };

    if (this.isSoldOut()) {
      this.TagView = ProductTagFactory.create(product_tag.sold_out, props);
      return;
    }

    if (this.isUpcoming()) {
      const value = this.data.sell_start_at;
      this.TagView = ProductTagFactory.create(product_tag.upcoming, value, props);
      return;
    }

    // set TagView (잔여수량, 잔여시간)
    if (/** MPS, DTG */ isLegacyStockProduct(this.data.goods_type_id)) {
      const availableStock = this.data.legacyStock.quantity - this.data.legacyStock.order_count;
      if (availableStock > 0 && availableStock < 100 && this.data.is_quantity_public) {
        this.TagView = ProductTagFactory.create(product_tag.quantity_limited, availableStock, props);
      } else {
        this.TagView = undefined;
      }
    } /** PBG, TPL */ else {
      if (
        this.data.stock.availableStock > 0 &&
        this.data.stock.isStockPublic &&
        !this.data.stock.isOutOfStock &&
        this.data.lowStockMeta &&
        this.data.stock.availableStock < this.data.lowStockMeta.threshold
      ) {
        this.TagView = ProductTagFactory.create(
          product_tag.quantity_limited,
          this.data.stock.availableStock,
          props,
        );
      } else if (this.data.sell_end_at) {
        /**
         * @figma
         * https://www.figma.com/design/Top00pi5q5ylSxEviOOciV/MarppleShop-Design-System?node-id=21842-60385&m=dev
         */
        // 1. 판매 기간 확인
        const isSaleStarted = this.data.sell_start_at ? this.data.sell_start_at < new Date() : true;
        const isSaleNotEnded = this.data.sell_end_at ? this.data.sell_end_at > new Date() : true;

        if (isSaleStarted && isSaleNotEnded) {
          this.TagView = new PeriodLimitedTag({
            ...props,
            value: this.data.sell_end_at,
            now_timestamp: Date.now(),
          });
        }
      } else {
        this.TagView = undefined;
      }
    }
  }

  private isUpcoming(): this is ProductCardView & {
    data: { sell_start_at: string; sell_end_at: string };
  } {
    return ProductCardHelper.isBeforeSale(this.data);
  }

  /**
   * 품절인지 아닌지 판별하는 함수
   */
  private isSoldOut(): boolean {
    return ProductCardHelper.isSoldOut(this.data);
  }

  private isPeriodLimited(): this is ProductCardView & {
    data: { sell_start_at: string; sell_end_at: string };
  } {
    return this.is_period_limited;
  }

  override redraw() {
    this.option.is_lazy = false;
    return super.redraw();
  }

  private getProductDetailUrl() {
    return `/${this.data.domain_name}/products/${this.data.id}`;
  }

  private makePostMessage(): PostMessageNavigate {
    return makeSubScreenNavigate(this.getProductDetailUrl());
  }

  public setRealtimeTagOption(option: Partial<ProductTagBaseProps>): this {
    this.realtimeTagOption = {
      ...this.realtimeTagOption,
      ...option,
    };
    return this;
  }
}
